import React, { useEffect, useState } from "react";
import {
    Table, TableBody, TableHead,
    Toolbar, Typography, CircularProgress, TablePagination,
    Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, MenuItem, Alert
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import Box from "@mui/material/Box";
import {
    getAppConfigs,
    getAppConfigDetails,
    updateAppConfig,
    createAppConfig,
    deleteAppConfig
} from "../services/appConfigService";
import {
    StyledTableContainer,
    StyledTableCell,
    StyledTableRow,
    StyledPaper,
} from "../assets/styles/tableStyle";
import FancyContextMenu, { ContextMenuAction } from "../components/FancyContextMenu";

type AppConfig = {
    id: number;
    key: string;
    value: string;
    description?: string;
    createdAt?: string;
    updatedAt?: string;
};

const allFields = ["key", "value", "description"] as const;
const MAP_PROVIDER_KEYS = ["GEOCODE_PROVIDER", "PLACES_PROVIDER", "ROUTES_PROVIDER"];
const JSON_KEYS = ["RIDER_SUBSIDY_CONFIG"];
// Keys owned by a dedicated UI — read-only here so they can't be desynced from their governor.
// LAUNCH_MODE_CITIES is governed by City Operations → Launch Mode (keeps the array + worker cache sane).
const GOVERNED_KEYS = ["LAUNCH_MODE_CITIES"];
const isGovernedKey = (key?: string | null) => !!key && GOVERNED_KEYS.includes(key);

const AppConfigList: React.FC = () => {
    const [items, setItems] = useState<AppConfig[]>([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState<number>(0);
    const [rowsPerPage, setRowsPerPage] = useState<number>(10);

    // Edit/Create modal state
    const [detailOpen, setDetailOpen] = useState<boolean>(false);
    const [selectedId, setSelectedId] = useState<number | null>(null);
    const [selectedItem, setSelectedItem] = useState<AppConfig | null>(null);
    const [editData, setEditData] = useState<Partial<AppConfig>>({});
    const [editLoading, setEditLoading] = useState<boolean>(false);
    const [modalMode, setModalMode] = useState<"edit"|"create">("edit");
    const [error, setError] = useState<string | null>(null);

    // Context menu
    const [menu, setMenu] = useState<{ top: number; left: number } | null>(null);
    const [selectedRow, setSelectedRow] = useState<AppConfig | null>(null);

    useEffect(() => { fetchItems(); }, []);

    const fetchItems = async () => {
        setLoading(true);
        try {
            const res = await getAppConfigs();
            setItems(res.data);
        } finally {
            setLoading(false);
        }
    };

    // MODAL HANDLING
    const openCreateModal = () => {
        setModalMode("create");
        setEditData({});
        setSelectedId(null);
        setSelectedItem(null);
        setError(null);
        setDetailOpen(true);
    };

    const handleRowClick = async (id: number) => {
        setModalMode("edit");
        setSelectedId(id);
        setDetailOpen(true);
        setEditLoading(true);
        setError(null);
        try {
            const res = await getAppConfigDetails(id);
            setSelectedItem(res.data);
            setEditData(res.data);
        } finally {
            setEditLoading(false);
        }
    };

    const handleCloseDetail = () => {
        setDetailOpen(false);
        setSelectedId(null);
        setSelectedItem(null);
        setEditData({});
        setError(null);
    };

    const handleFieldChange = (field: keyof AppConfig, value: string) => {
        setEditData((prev) => ({ ...prev, [field]: value }));
        if (error) setError(null);
    };

    const handleSaveEdit = async () => {
        // 0. Governed keys are managed from their own UI — never write them here.
        if (isGovernedKey(editData.key)) {
            setError(`'${editData.key}' is managed from City Operations → Launch Mode. Edit it there.`);
            return;
        }
        // 1. Check if JSON fields are valid
        if (editData.key && JSON_KEYS.includes(editData.key) && editData.value) {
            try {
                JSON.parse(editData.value);
            } catch (e) {
                setError(`Invalid JSON format for ${editData.key}. Please check syntax.`);
                return; // Stop save
            }
        }

        setEditLoading(true);
        try {
            const updateFields: any = {};
            allFields.forEach((field) => {
                if (editData[field] !== undefined) updateFields[field] = editData[field];
            });
            if (modalMode === "edit" && selectedId) {
                await updateAppConfig(selectedId, updateFields);
            } else if (modalMode === "create") {
                await createAppConfig(updateFields);
            }
            await fetchItems();
            setDetailOpen(false);
        } catch (e) {
            console.error(e);
            setError("Failed to save configuration.");
        } finally {
            setEditLoading(false);
        }
    };

    // CONTEXT MENU
    const handleContextMenu = (event: React.MouseEvent, row: AppConfig) => {
        event.preventDefault();
        setSelectedRow(row);
        setMenu({ top: event.clientY, left: event.clientX });
    };

    const handleCloseMenu = () => setMenu(null);

    const handleEditContext = () => {
        if (selectedRow) {
            handleRowClick(selectedRow.id);
        }
        handleCloseMenu();
    };

    const handleDeleteContext = async () => {
        if (selectedRow && isGovernedKey(selectedRow.key)) {
            handleCloseMenu();
            window.alert(`'${selectedRow.key}' is managed from City Operations → Launch Mode. Remove cities there.`);
            return;
        }
        if (selectedRow) {
            setLoading(true);
            try {
                await deleteAppConfig(selectedRow.id);
                await fetchItems();
            } finally {
                setLoading(false);
            }
        }
        handleCloseMenu();
    };

    const paginatedItems = items.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    const actions: ContextMenuAction[] = [
        {
            dividerBefore: true,
            label: "Edit",
            icon: <EditIcon color="info" fontSize="small" />,
            onClick: handleEditContext,
            color: "info",
        },
        {
            label: "Delete",
            icon: <DeleteIcon color="error" fontSize="small" />,
            onClick: handleDeleteContext,
            color: "error",
            dividerBefore: true,
        }
    ];

    return (
        <StyledPaper sx={{ maxWidth: "98vw", width: "100%", margin: '0 auto', my: 2 }}>
            <Toolbar sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Typography variant="h6">App Config Management</Typography>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={openCreateModal}
                    sx={{ ml: 2 }}
                >Add New</Button>
            </Toolbar>
            {loading ? (
                <CircularProgress sx={{ margin: 2 }} />
            ) : (
                <StyledTableContainer>
                    <Table sx={{ minWidth: 1000 }}>
                        <TableHead>
                            <StyledTableRow>
                                <StyledTableCell>ID</StyledTableCell>
                                {allFields.map(col => <StyledTableCell key={col}>{col.charAt(0).toUpperCase() + col.slice(1)}</StyledTableCell>)}
                                <StyledTableCell>Created At</StyledTableCell>
                                <StyledTableCell>Updated At</StyledTableCell>
                            </StyledTableRow>
                        </TableHead>
                        <TableBody>
                            {paginatedItems.map((item) => (
                                <StyledTableRow
                                    key={item.id}
                                    hover
                                    sx={{ cursor: "pointer" }}
                                    onClick={() => handleRowClick(item.id)}
                                    onContextMenu={e => handleContextMenu(e, item)}
                                >
                                    <StyledTableCell>{item.id}</StyledTableCell>
                                    {allFields.map(field => (
                                        <StyledTableCell key={field}>
                                            {field === "key" && isGovernedKey(item.key) ? `🔒 ${item.key}` : null}
                                            {field === "key" && isGovernedKey(item.key) ? null : (
                                                field === "value" && item.value && item.value.length > 18
                                                    ? item.value.slice(0, 18) + "..."
                                                    : item[field as keyof AppConfig] ?? "-"
                                            )}
                                        </StyledTableCell>
                                    ))}
                                    <StyledTableCell>{item.createdAt ? item.createdAt.slice(0, 19) : "-"}</StyledTableCell>
                                    <StyledTableCell>{item.updatedAt ? item.updatedAt.slice(0, 19) : "-"}</StyledTableCell>
                                </StyledTableRow>
                            ))}
                        </TableBody>
                    </Table>
                    <TablePagination
                        rowsPerPageOptions={[10, 25, 50]}
                        component="div"
                        count={items.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={(e, newPage) => setPage(newPage)}
                        onRowsPerPageChange={(e) => {
                            setRowsPerPage(parseInt(e.target.value, 10));
                            setPage(0);
                        }}
                    />
                </StyledTableContainer>
            )}
            {/* Edit/Create Modal */}
            <Dialog open={detailOpen} onClose={handleCloseDetail} fullWidth maxWidth="md">
                <DialogTitle>{modalMode === "edit" ? "Edit App Config" : "Create App Config"}</DialogTitle>
                <DialogContent>
                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                    {editLoading ? <CircularProgress /> : (
                        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 2, mt: 1 }}>
                            {modalMode === "edit" && selectedItem && (
                                <>
                                    <TextField label="ID" value={selectedItem.id} fullWidth disabled />
                                    <TextField label="Created At" value={selectedItem.createdAt || ""} fullWidth disabled />
                                    <TextField label="Updated At" value={selectedItem.updatedAt || ""} fullWidth disabled />
                                </>
                            )}
                            {allFields.map((field) => {
                                const isProviderValue = field === 'value' && MAP_PROVIDER_KEYS.includes(editData.key || "");
                                return isProviderValue ? (
                                    <TextField
                                        key={field}
                                        select
                                        label={field.charAt(0).toUpperCase() + field.slice(1)}
                                        value={editData[field as keyof AppConfig] ?? ""}
                                        onChange={e => handleFieldChange(field as keyof AppConfig, e.target.value)}
                                        fullWidth
                                    >
                                        <MenuItem value="google">Google</MenuItem>
                                        <MenuItem value="osm">ZAPPPIT-OSM</MenuItem>
                                    </TextField>
                                ) : (
                                    <TextField
                                        key={field}
                                        label={field.charAt(0).toUpperCase() + field.slice(1)}
                                        value={editData[field as keyof AppConfig] ?? ""}
                                        onChange={e => handleFieldChange(field as keyof AppConfig, e.target.value)}
                                        fullWidth
                                        // Auto-expand for JSON or long strings
                                        multiline={
                                            (field === "value" && (JSON_KEYS.includes(editData.key || "") || (typeof editData.value === "string" && editData.value.length > 20)))
                                            || field === "description"
                                        }
                                        minRows={JSON_KEYS.includes(editData.key || "") ? 4 : 1}
                                        // Key is never renamed in edit; governed keys (e.g. LAUNCH_MODE_CITIES) are read-only here.
                                        disabled={(modalMode === "edit" && field === "key") || (field !== "key" && isGovernedKey(editData.key))}
                                        helperText={
                                            field === "value" && isGovernedKey(editData.key)
                                                ? "🔒 Managed from City Operations → Launch Mode. Edit it there."
                                                : undefined
                                        }
                                    />
                                );
                            })}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDetail}>Close</Button>
                    <Button
                        variant="contained"
                        onClick={handleSaveEdit}
                        disabled={editLoading || isGovernedKey(editData.key)}
                    >
                        {modalMode === "edit" ? "Save" : "Create"}
                    </Button>
                </DialogActions>
            </Dialog>
            <FancyContextMenu
                actions={actions}
                open={Boolean(menu)}
                onClose={handleCloseMenu}
                anchorPosition={menu}
                title={selectedRow?.key || "Row Actions"}
            />
        </StyledPaper>
    );
};

export default AppConfigList;
